import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { getSEOMeta } from './seoMetadata';

function setMeta(attr, attrVal, content) {
  let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * SEOManager — renders nothing, manages all document <head> tags.
 * Place inside <BrowserRouter>.
 */
export function SEOManager() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();

  useEffect(() => {
    const meta = getSEOMeta(location.pathname, lang, searchParams);

    // <html lang>
    document.documentElement.lang = meta.lang;

    // <title>
    document.title = meta.title;

    // Standard meta
    setMeta('name', 'description', meta.description);

    // Canonical
    setLink('canonical', meta.canonicalUrl);

    // Hreflang alternates
    setLink('alternate', meta.enUrl, 'en');
    setLink('alternate', meta.esUrl, 'es');
    setLink('alternate', meta.enUrl, 'x-default');

    // Open Graph
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'YBV Sports');
    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:url', meta.canonicalUrl);
    setMeta('property', 'og:locale', meta.ogLocale);
    setMeta('property', 'og:locale:alternate', meta.ogLocaleAlt);
    setMeta('property', 'og:image', meta.ogImage);
    setMeta('property', 'og:image:width', '1536');
    setMeta('property', 'og:image:height', '1024');

    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', meta.title);
    setMeta('name', 'twitter:description', meta.description);
    setMeta('name', 'twitter:image', meta.ogImage);
  }, [location.pathname, lang, searchParams]);

  return null;
}

/**
 * LanguageURLSync — keeps ?lang= in the URL in sync with the language state.
 * On every route change, ensures ?lang=es is present if Spanish is active.
 * Place inside <BrowserRouter>.
 */
export function LanguageURLSync() {
  const location = useLocation();
  const { lang } = useLanguage();

  useEffect(() => {
    const url = new URL(window.location);
    const currentParam = url.searchParams.get('lang');

    if (lang === 'es' && currentParam !== 'es') {
      url.searchParams.set('lang', 'es');
      window.history.replaceState({}, '', url);
    } else if (lang !== 'es' && currentParam) {
      url.searchParams.delete('lang');
      window.history.replaceState({}, '', url);
    }
  }, [location.pathname, location.search, lang]);

  return null;
}
